import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  HStack,
  Modal,
  theme,
  useResponsive,
} from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationConfirmExitModal = (props: Props) => {
  const {
    isVisible,
    title,
    body,
    confirmButtonTitle,
    cancelButtonTitle,
    target,
    size = 'md',
    onConfirm,
    onCancel,
    onShow,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const formattedTitle = title
    ? title.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.title, { target });
  const formattedBody = body
    ? body.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.body, { target });
  const formattedConfirmButtonTitle = confirmButtonTitle
    ? confirmButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.returnTo, { target });
  const formattedCancelButtonTitle = cancelButtonTitle
    ? cancelButtonTitle.replaceAll(DYNAMIC_TARGET_TEXT, target)
    : formatMessage(messages.continueCustomization);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="medium"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onCancel}
    >
      <CustomizationModalContentContainer size={size}>
        <CustomizationModalHeading>{formattedTitle}</CustomizationModalHeading>

        <BodyText sizeMatch={['16']}>{formattedBody}</BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size={match(['large', 'large-wide'])}
              palette="secondary"
              onPress={onConfirm}
              style={size === 'lg' ? styles.ctaLG : styles.cta}
            >
              {formattedConfirmButtonTitle}
            </Button>

            <Button
              size={match(['large', 'large-wide'])}
              palette="primary"
              onPress={onCancel}
              style={size === 'lg' ? styles.ctaLG : styles.cta}
            >
              {formattedCancelButtonTitle}
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalLabel: {
    defaultMessage: 'Confirm exit modal',
    description: 'Customization | Confirm exit modal | Label',
  },
  title: {
    defaultMessage: 'Are you sure you want to return to {target}?',
    description: 'Customization | Confirm exit modal | Heading',
  },
  body: {
    defaultMessage: 'You will lose any customizations you’ve made.',
    description: 'Customization | Confirm exit modal | Body',
  },
  returnTo: {
    defaultMessage: 'Yes, return to {target}',
    description: 'Customization | Confirm exit modal | Return button label',
  },
  continueCustomization: {
    defaultMessage: 'No, continue to customize',
    description:
      'Customization | Confirm exit modal | Continue customization button label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DYNAMIC_TARGET_TEXT = '(target)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
    paddingBottom: Platform.select({
      android: theme.spacing['6'],
      default: 0,
    }),
  },
  cta: {
    paddingHorizontal: theme.spacing['1'],
  },
  ctaLG: {
    paddingHorizontal: theme.spacing['1'],
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  title: string | undefined;
  body: string | undefined;
  confirmButtonTitle: string | undefined;
  cancelButtonTitle: string | undefined;
  target: string;
  size?: 'md' | 'lg';
  onConfirm: () => void;
  onCancel: () => void;
  onShow?: () => void;
};
